import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

import Navbar from './components/Navbar';
import Login from './components/Login';
import Home from './pages/Home';
import CompoundsIndex from './pages/CompoundsIndex';
import CompoundDetail from './pages/CompoundDetail';
import CategoriesIndex from './pages/CategoriesIndex';
import CategoryDetail from './pages/CategoryDetail';
import About from './pages/About';
import Journal from './pages/Journal';
import Admin from './pages/Admin';
import Dashboard from './pages/Dashboard';

// Protected route wrapper — redirects to /login if not authenticated
function PrivateRoute({ children }) {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
        flexDirection: 'column',
        gap: 16,
      }}>
        <div className="spinner" />
        <span style={{ color: '#64748b', fontSize: 14 }}>Loading…</span>
      </div>
    );
  }

  return currentUser ? children : <Navigate to="/login" replace />;
}

// Public route — redirects to home if already authenticated
function PublicRoute({ children }) {
  const { currentUser, loading } = useAuth();

  if (loading) return null;
  return currentUser ? <Navigate to="/" replace /> : children;
}

// Layout wrapper that includes the Navbar
function AppLayout({ children }) {
  return (
    <>
      <Navbar />
      {children}
    </>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        {/* Public: Login */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected: All app routes */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <AppLayout>
                <Home />
              </AppLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/compounds"
          element={
            <PrivateRoute>
              <AppLayout>
                <CompoundsIndex />
              </AppLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/compounds/:compoundId"
          element={
            <PrivateRoute>
              <AppLayout>
                <CompoundDetail />
              </AppLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/categories"
          element={
            <PrivateRoute>
              <AppLayout>
                <CategoriesIndex />
              </AppLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/categories/:categoryId"
          element={
            <PrivateRoute>
              <AppLayout>
                <CategoryDetail />
              </AppLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/journal"
          element={
            <PrivateRoute>
              <AppLayout>
                <Journal />
              </AppLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/about"
          element={
            <PrivateRoute>
              <AppLayout>
                <About />
              </AppLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <AppLayout>
                <Admin />
              </AppLayout>
            </PrivateRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </PrivateRoute>
          }
        />

        {/* Catch-all: redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}